import type { ReactNode } from 'react';

type Tone = 'neutral' | 'pending' | 'approved' | 'rejected';

const TONES: Record<Tone, string> = {
  neutral: 'border-border text-muted-foreground',
  pending: 'border-warning/40 bg-warning/10 text-warning',
  approved: 'border-success/40 bg-success/10 text-success',
  rejected: 'border-danger/40 bg-danger/10 text-danger',
};

/**
 * State as a shape, not only as a word.
 *
 * §5 wants a panel that is scanned under time pressure, and colour alone is not enough:
 * the dot plus the border give the chip a form that reads at a glance and still works
 * for an operator who cannot distinguish the hues.
 */
export function Chip({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${TONES[tone]}`}
    >
      <span className="size-1.5 rounded-full bg-current" aria-hidden="true" />
      {children}
    </span>
  );
}

export function toneForApproval(status: string): Tone {
  switch (status) {
    case 'Approved':
      return 'approved';
    case 'Rejected':
      return 'rejected';
    case 'Pending':
      return 'pending';
    default:
      return 'neutral';
  }
}

/**
 * ⚠️ The tones say "does this need attention", not "is this good news".
 *
 * `Active` is the success tone because a search that is running is the state the charity wants; `Draft`
 * and `PendingCommittee` are the warning tone because both are a person waiting on a decision.
 * `Closed` is neutral rather than green — a mission can close with any of §23.3's seven outcomes,
 * including the ones nobody wants, and painting them all as success would be a colour that lies.
 */
export function toneForMissionStatus(status: string): Tone {
  switch (status) {
    case 'Active':
      return 'approved';
    case 'Draft':
    case 'PendingCommittee':
      return 'pending';
    case 'Cancelled':
      return 'rejected';
    default:
      return 'neutral';
  }
}
